/**
 * PolicyPage Component
 * Reusable component to display policy documents from markdown files
 */

import { Box, Typography, Paper, Button, Stack } from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface PolicyPageProps {
  title: string;
  content: string;
}

export function PolicyPage({ title, content }: PolicyPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  // Scroll to top when the component mounts or route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box
      sx={{
        minHeight: '120vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        overflowX: 'hidden',
      }}
    >
      {/* Header - matching LoginPage */}
      <Box
        component="header"
        sx={{
          background: 'linear-gradient(180deg, #fff8ef 0%, #f5f0e8 100%)',
          border: '1px solid',
          borderColor: 'rgba(4, 55, 123, 0.3)',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          py: 2,
          px: { xs: 3, md: 6 },
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backdropFilter: 'blur(10px)',
          boxShadow: `
            0 0 0 1px rgba(4, 55, 123, 0.15),
            0 0 8px rgba(4, 55, 123, 0.2),
            0 0 15px rgba(4, 55, 123, 0.15),
            0 3px 16px rgba(4, 55, 123, 0.25),
            0 1px 6px rgba(4, 55, 123, 0.15),
            inset 0 0 10px rgba(255, 255, 255, 0.03),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(4, 55, 123, 0.1)
          `,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          height: '64px',
          minHeight: '64px',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          width: '100%',
        }}
      >
        <Box
          component="img"
          src="/assets/flowrithm-logo.png"
          alt="FlowRithm logo"
          sx={{ height: 32 }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.25, md: 2 },
            flex: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Button
            component={Link}
            to="/login"
            variant="text"
            color="inherit"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: '#04377b',
              '&:hover': {
                bgcolor: 'rgba(4, 55, 123, 0.05)',
              },
            }}
          >
            Home
          </Button>
        </Box>
      </Box>

      {/* Main Content - matching LoginPage background */}
      <Box
        component="main"
        sx={{
          flex: 1,
          position: 'relative',
          minHeight: { xs: 'auto', md: '100vh' },
          backgroundImage: 'url(/assets/login-hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: { xs: 'center top', md: 'left center' },
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          px: { xs: 3, md: 10 },
          py: { xs: 6, md: 8 },
          pb: { xs: 10, md: 20 },
          pt: { xs: 10, md: 10 },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0.2) 65%, rgba(255,255,255,0) 100%)',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 900,
            position: 'relative',
            zIndex: 1,
            mt: { xs: 2, md: 4 },
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 3, textTransform: 'none' }}
          >
            Back
          </Button>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 2,
              backgroundColor: '#fff',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#04377b',
                mb: 3,
                fontSize: { xs: '1.75rem', md: '2rem' },
              }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                fontSize: '0.9375rem',
                lineHeight: 1.7,
                color: '#616161',
              }}
            >
              {content.split('\n').map((line, index) => {
                // Handle markdown headers (# Header)
                if (line.match(/^#{1,3}\s/)) {
                  const level = line.match(/^#+/)?.[0].length || 1;
                  const text = line.replace(/^#+\s/, '');
                  return (
                    <Typography
                      key={index}
                      variant={level === 1 ? 'h5' : level === 2 ? 'h6' : 'subtitle1'}
                      sx={{
                        fontWeight: 700,
                        color: '#212121',
                        mt: index > 0 ? 3 : 0,
                        mb: 1.5,
                      }}
                    >
                      {text}
                    </Typography>
                  );
                }
                // Handle numbered section headings (e.g., "1. INTRODUCTION", "2. ACCEPTANCE OF TERMS", "1. No Broker / No Execution Guarantee")
                // Pattern: starts with number, period, space, then text
                // Section headings are typically short lines (less than 100 chars) that are standalone
                if (line.match(/^\d+\.\s+/)) {
                  const trimmedLine = line.trim();
                  const textAfterNumber = trimmedLine.replace(/^\d+\.\s+/, '');
                  
                  // Check if it's likely a section heading:
                  // - Short line (less than 100 chars) - headings are usually concise
                  // - Not a full paragraph (doesn't contain multiple sentences or end with period followed by lowercase)
                  // - Not list content (doesn't contain bullet markers)
                  const isShortLine = trimmedLine.length < 100;
                  const isNotParagraph = !trimmedLine.match(/[.!?]\s+[a-z]/); // Doesn't have sentence ending followed by lowercase
                  const isNotListContent = !trimmedLine.includes('•') && !trimmedLine.includes('*');
                  const hasHeadingLikeStructure = textAfterNumber.length > 0 && textAfterNumber.length < 80;
                  
                  if (isShortLine && isNotParagraph && isNotListContent && hasHeadingLikeStructure) {
                    return (
                      <Typography
                        key={index}
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#212121',
                          mt: index > 0 ? 3 : 0,
                          mb: 1.5,
                        }}
                      >
                        {trimmedLine}
                      </Typography>
                    );
                  }
                }
                // Handle bullet list items
                if (line.match(/^[\*\-\•]\s/)) {
                  const text = line.replace(/^[\*\-\•]\s+/, '');
                  return (
                    <Box
                      key={index}
                      component="li"
                      sx={{
                        color: '#616161',
                        lineHeight: 1.7,
                        mb: 0.5,
                        ml: 2,
                      }}
                    >
                      {text}
                    </Box>
                  );
                }
                // Handle bold text
                if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
                  const text = line.replace(/\*\*/g, '');
                  return (
                    <Typography
                      key={index}
                      component="strong"
                      sx={{
                        fontWeight: 600,
                        color: '#212121',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      {text}
                    </Typography>
                  );
                }
                // Handle separator lines
                if (line.trim() === '________________________________________' || line.trim() === '---') {
                  return <Box key={index} sx={{ borderTop: '1px solid #e0e0e0', my: 3 }} />;
                }
                // Regular paragraph
                if (line.trim()) {
                  return (
                    <Typography
                      key={index}
                      sx={{
                        color: '#616161',
                        lineHeight: 1.7,
                        mb: 1.5,
                      }}
                    >
                      {line}
                    </Typography>
                  );
                }
                // Empty lines
                return <Box key={index} sx={{ mb: 1 }} />;
              })}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Footer - matching LoginPage */}
      <Box
        component="footer"
        sx={{
          background: 'linear-gradient(180deg, #fff8ef 0%, #f5f0e8 100%)',
          color: '#04377b',
          mt: { xs: 4, md: 6 },
          px: { xs: 3, md: 5 },
          py: { xs: 1.9, md: 2 },
          border: '1px solid',
          borderColor: 'rgba(4, 55, 123, 0.3)',
          borderBottom: 'none',
          borderRadius: '8px 8px 0 0',
          boxShadow: `
            0 0 0 1px rgba(4, 55, 123, 0.15),
            0 0 8px rgba(4, 55, 123, 0.2),
            0 0 15px rgba(4, 55, 123, 0.15),
            0 -3px 16px rgba(4, 55, 123, 0.25),
            0 -1px 6px rgba(4, 55, 123, 0.15),
            inset 0 0 10px rgba(255, 255, 255, 0.03),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(4, 55, 123, 0.1)
          `,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                component="img"
                src="/assets/flowrithm-logo.png"
                alt="flowrithm"
                sx={{
                  height: { xs: 24, sm: 28 },
                  width: 'auto',
                }}
              />
              <Typography component="span" sx={{ fontWeight: 400, color: '#04377b', fontSize: '0.75rem' }}>
                Powered By
              </Typography>
              <Button
                href="https://flowrithm.trade"
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                sx={{
                  color: '#04377b',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  textTransform: 'none',
                  p: 0,
                  minWidth: 0,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                lagori.ai
              </Button>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(4, 55, 123, 0.8)' }}>
              Unified Broker Access | Agentic AI Workflows | Pro-Grade Security
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'rgba(4, 55, 123, 0.8)' }}>
            contact@flowrithm.trade | +91 98866 44123
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 1.25,
            pt: 1,
            borderTop: '1px solid rgba(4, 55, 123, 0.2)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            fontSize: '0.75rem',
            color: 'rgba(4, 55, 123, 0.75)',
          }}
        >
          <Typography variant="body2">© {currentYear} flowRithm · Built in Bengaluru.</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              component={Link}
              to="/policy/cookie"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Cookie Policy
            </Button>
            <Button
              component={Link}
              to="/policy/disclaimer"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Disclaimer
            </Button>
            <Button
              component={Link}
              to="/policy/privacy"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Privacy Policy
            </Button>
            <Button
              component={Link}
              to="/policy/disclosure"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Disclosure Agreement
            </Button>
            <Button
              component={Link}
              to="/policy/terms"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Terms of Use
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

